import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Stack } from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { Skeleton } from '@liminal/web-ui/feedback/Skeleton';
import { EmptyState } from '@liminal/web-ui/feedback/EmptyState';
import { ErrorState } from '@liminal/web-ui/feedback/ErrorState';
import { WarningCard } from '@liminal/web-ui/feedback/WarningCard';
import { Button } from '@liminal/web-ui/primitives/Button';
import { IconButton } from '@liminal/web-ui/primitives/IconButton';
import { styled } from '@mui/material/styles';
import { useConversationDetail } from '@/hooks/useConversations';
import { useConversationFlags } from '@/hooks/useConversationFlags';
import { formatOrderDate } from '@liminal/utils/date';
import { UserIdLink } from '@/components/ui/UserIdLink';
import { getErrorMessage } from '@/errors/messages';
import type { ConversationFlag, ConversationMessage } from '@/api/types';
import { borderRadius, fontSizes, fontWeights, letterSpacing, motion } from '@liminal/brand/scale';
import { webColors } from '@liminal/brand/semantic';
import { shadows } from '@liminal/brand/shadows';
import { ConversationFlagNotes } from '../components/ConversationFlagNotes';
import { FlagComposer } from '../components/FlagComposer';
import { anchoredMessageId, messageAnchorId } from '../components/conversationFlags';

// The most flags the API lists at once, so a conversation's flags are one page.
const FLAGS_PAGE_SIZE = 100;

// The flag button on a reply: hidden until the message is hovered, or the
// button itself has keyboard focus, so reading the thread stays quiet.
const FLAG_ACTION_CLASS = 'message-flag-action';

// How wide the flag panel is beside the thread.
const PANEL_WIDTH = 360;

const Thread = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(2),
  padding: theme.spacing(3),
}));

// The thread and, while a flag is being written, the panel it is written in.
// On a desktop the panel is a second column that stays in view as the thread
// scrolls, so the admin can read the whole conversation while writing. On a
// phone there is no room beside the thread, so the panel is a sheet fixed to
// the bottom of the screen instead, over the thread's padded end. Neither is
// modal: the thread stays scrollable either way.
const Body = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'composing',
})<{ composing: boolean }>(({ theme, composing }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  columnGap: theme.spacing(3),
  alignItems: 'start',
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: composing ? `minmax(0, 1fr) ${PANEL_WIDTH}px` : 'minmax(0, 1fr)',
  },
}));

// The flags and the thread. On a phone, while the sheet is open, padded at the
// end by about the sheet's height so it hides none of the thread.
const Main = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'composing',
})<{ composing: boolean }>(({ theme, composing }) => ({
  gridArea: '1 / 1',
  gap: theme.spacing(3),
  paddingBottom: composing ? theme.spacing(36) : 0,
  [theme.breakpoints.up('md')]: { paddingBottom: 0 },
}));

const FlagPanel = styled(Box)(({ theme }) => ({
  zIndex: theme.zIndex.appBar,
  backgroundColor: webColors.surface.primary,
  border: `1px solid ${webColors.border.light}`,
  padding: theme.spacing(2),
  // The sheet: across the bottom of the screen, the thread scrolling above it.
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  borderRadius: `${borderRadius.xl}px ${borderRadius.xl}px 0 0`,
  boxShadow: shadows.md,
  [theme.breakpoints.up('md')]: {
    // The column: beside the thread, pinned to the top of the viewport.
    position: 'sticky',
    left: 'auto',
    right: 'auto',
    bottom: 'auto',
    top: theme.spacing(3),
    gridArea: '1 / 2',
    alignSelf: 'start',
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: shadows.sm,
  },
}));

// The reply being flagged, quoted in the panel: enough to know which one,
// clamped so a long reply does not push the field off the screen.
const QuotedReply = styled(Typography)({
  display: '-webkit-box',
  WebkitLineClamp: 4,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  whiteSpace: 'pre-wrap',
});

// One message and its flags. User messages sit right, assistant and system
// messages left. The message a link points at is tinted, with the tint spread
// past its edges so the layout does not move.
interface MessageRowProps {
  fromUser: boolean;
  highlighted: boolean;
  composing: boolean;
}

const MessageRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'fromUser' && prop !== 'highlighted' && prop !== 'composing',
})<MessageRowProps>(({ theme, fromUser, highlighted, composing }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: fromUser ? 'flex-end' : 'flex-start',
  gap: theme.spacing(1),
  borderRadius: theme.shape.borderRadius * 2,
  [`& .${FLAG_ACTION_CLASS}`]: {
    opacity: 0,
    transition: `opacity ${motion.duration.fast}ms`,
  },
  [`&:hover .${FLAG_ACTION_CLASS}, & .${FLAG_ACTION_CLASS}:focus-within`]: { opacity: 1 },
  // No hover on a touch screen, so the button is always there.
  '@media (hover: none)': { [`& .${FLAG_ACTION_CLASS}`]: { opacity: 1 } },
  // The reply being flagged keeps its button, marking which one the panel is about.
  ...(composing && { [`& .${FLAG_ACTION_CLASS}`]: { opacity: 1 } }),
  ...(highlighted && {
    backgroundColor: webColors.state.warningBg,
    boxShadow: `0 0 0 ${theme.spacing(1)} ${webColors.state.warningBg}`,
  }),
}));

// The bubble and its flag button, kept to 80% of the thread's width.
const BubbleLine = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(0.5),
  maxWidth: '80%',
}));

// A single message bubble, coloured by role.
const Bubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'role',
})<{ role: string }>(({ theme, role }) => {
  const isUser = role === 'user';
  return {
    minWidth: 0,
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(1.5, 2),
    backgroundColor: isUser
      ? theme.palette.primary.main
      : role === 'system'
        ? theme.palette.action.hover
        : theme.palette.background.default,
    color: isUser ? theme.palette.primary.contrastText : theme.palette.text.primary,
    border: isUser ? 'none' : `1px solid ${theme.palette.divider}`,
  };
});

const RoleLabel = styled(Typography)({
  fontSize: fontSizes.xs,
  fontWeight: fontWeights.semibold,
  textTransform: 'uppercase',
  letterSpacing: `${letterSpacing.wide}px`,
  opacity: 0.7,
});

function toolNames(msg: ConversationMessage): string {
  return (msg.toolsUsed ?? [])
    .map((t) => t.tool)
    .filter((name): name is string => !!name)
    .join(', ');
}

interface MessageBubbleProps {
  msg: ConversationMessage;
  /** The flags on this message, newest first. */
  flags: readonly ConversationFlag[];
  /** A link points at this message. */
  highlighted: boolean;
  /** This message is being flagged in the panel. */
  composing: boolean;
  onFlag: () => void;
}

function MessageBubble({ msg, flags, highlighted, composing, onFlag }: MessageBubbleProps) {
  const role = msg.role ?? 'assistant';
  const tools = toolNames(msg);
  // Nim's replies are flagged one by one; anything else is flagged with the
  // whole conversation, from the header.
  const flaggable = role === 'assistant' && !!msg.id;
  return (
    <MessageRow
      id={msg.id ? messageAnchorId(msg.id) : undefined}
      fromUser={role === 'user'}
      highlighted={highlighted}
      composing={composing}
      aria-current={highlighted ? 'true' : undefined}
      data-testid={`message-${msg.id}`}
    >
      <BubbleLine>
        <Bubble role={role}>
          <RoleLabel>
            {role}
            {msg.createdAt ? ` · ${formatOrderDate(msg.createdAt)}` : ''}
          </RoleLabel>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
            {msg.content || '—'}
          </Typography>
          {tools && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.6 }}>
              tools: {tools}
            </Typography>
          )}
        </Bubble>
        {flaggable && (
          <Box className={FLAG_ACTION_CLASS}>
            <IconButton icon="flag" label="Flag this reply" size="sm" tone="muted" onClick={onFlag} />
          </Box>
        )}
      </BubbleLine>
      {flags.length > 0 && (
        <Box sx={{ maxWidth: '80%' }}>
          <ConversationFlagNotes flags={flags} />
        </Box>
      )}
    </MessageRow>
  );
}

export function ConversationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const conversationId = id ?? '';
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { data, isLoading, isError } = useConversationDetail(conversationId);
  const flagsQuery = useConversationFlags({
    conversationId,
    pageSize: FLAGS_PAGE_SIZE,
    enabled: !!conversationId,
  });
  // What the flag panel is open for: a reply (its messageId), the whole
  // conversation (no messageId), or nothing (null). One at a time.
  const [composingFor, setComposingFor] = useState<{ messageId?: string } | null>(null);

  const conversation = data?.conversation;
  const messages = data?.messages ?? [];
  const flags = flagsQuery.data?.flags ?? [];

  // Each flag under the message it is on. A whole-conversation flag goes at
  // the top, and so would one whose message is not in the thread, quoting it,
  // rather than vanish.
  const messageIds = new Set(messages.map((msg) => msg.id));
  const flagsByMessage = new Map<string, ConversationFlag[]>();
  const conversationFlags: ConversationFlag[] = [];
  for (const flag of flags) {
    if (flag.messageId && messageIds.has(flag.messageId)) {
      flagsByMessage.set(flag.messageId, [...(flagsByMessage.get(flag.messageId) ?? []), flag]);
    } else {
      conversationFlags.push(flag);
    }
  }
  // The reply the panel is about, quoted there so the admin can scroll away from it.
  const composingMessage =
    composingFor?.messageId === undefined ? undefined : messages.find((msg) => msg.id === composingFor.messageId);

  // A link to /conversations/<id>#message-<messageId> lands on that message,
  // once the thread and its flags have loaded and the layout has settled.
  const anchoredId = anchoredMessageId(hash);
  const anchorFound = anchoredId !== '' && messageIds.has(anchoredId);
  const layoutSettled = !isLoading && !flagsQuery.isLoading;
  useEffect(() => {
    if (!anchorFound || !layoutSettled) return;
    document.getElementById(messageAnchorId(anchoredId))?.scrollIntoView({ block: 'center' });
  }, [anchoredId, anchorFound, layoutSettled]);

  return (
    <Stack spacing={3}>
      <Box>
        <Button variant="link" size="sm" startIcon="arrow-back" onClick={() => navigate('/conversations')}>
          Back to Conversations
        </Button>
      </Box>

      {isLoading ? (
        <Card variant="elevated">
          <Skeleton width="40%" height={32} />
          <Skeleton width="25%" />
          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height={48} />
            ))}
          </Box>
        </Card>
      ) : isError ? (
        // A failed request and a missing conversation are different facts;
        // both used to read "Conversation not found".
        <Card variant="elevated">
          <ErrorState message="Could not load this conversation." />
        </Card>
      ) : !conversation ? (
        <Card variant="elevated">
          <EmptyState icon="search" title="Conversation not found" />
        </Card>
      ) : (
        <>
          <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
            <Stack spacing={0.5}>
              <Typography variant="h5">{conversation.title || 'Untitled'}</Typography>
              <Typography variant="body2" color="text.disabled">
                {conversation.email || 'Unknown user'}
                {' · '}
                <UserIdLink userId={conversation.userId} />
              </Typography>
            </Stack>
            <Button variant="secondary" size="sm" startIcon="flag" onClick={() => setComposingFor({})}>
              Flag conversation
            </Button>
          </Stack>

          {flagsQuery.isError && (
            <WarningCard tone="error">
              Could not load this conversation&apos;s flags: {getErrorMessage(flagsQuery.error)}
            </WarningCard>
          )}
          {!!flagsQuery.data?.nextPageToken && (
            <WarningCard>
              This conversation has more than {FLAGS_PAGE_SIZE} flags. Only the newest {FLAGS_PAGE_SIZE} are shown.
            </WarningCard>
          )}
          {anchoredId !== '' && !anchorFound && (
            <WarningCard>The linked message is not in this conversation.</WarningCard>
          )}

          <Body composing={composingFor !== null}>
            <Main composing={composingFor !== null}>
              {conversationFlags.length > 0 && (
                <Card variant="elevated" data-testid="conversation-flags">
                  <Stack spacing={2}>
                    <Typography variant="subtitle2">Flags on this conversation</Typography>
                    <ConversationFlagNotes flags={conversationFlags} quoteMessage />
                  </Stack>
                </Card>
              )}

              <Card variant="elevated">
                {messages.length > 0 ? (
                  <Thread>
                    {messages.map((msg, i) => (
                      <MessageBubble
                        key={msg.id ?? i}
                        msg={msg}
                        flags={msg.id ? (flagsByMessage.get(msg.id) ?? []) : []}
                        highlighted={!!msg.id && msg.id === anchoredId}
                        composing={!!msg.id && composingFor?.messageId === msg.id}
                        onFlag={() => setComposingFor({ messageId: msg.id })}
                      />
                    ))}
                  </Thread>
                ) : (
                  <EmptyState icon="search" size="sm" title="This conversation has no messages" />
                )}
              </Card>
            </Main>

            {composingFor !== null && (
              <FlagPanel data-testid="flag-panel">
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2">
                      {composingFor.messageId === undefined ? 'Flag this conversation' : 'Flag this reply'}
                    </Typography>
                    <IconButton
                      icon="close"
                      label="Close"
                      size="sm"
                      tone="muted"
                      onClick={() => setComposingFor(null)}
                    />
                  </Stack>
                  {composingMessage && (
                    <Box sx={{ borderLeft: `2px solid ${webColors.border.dark}`, pl: 1.5, py: 0.5 }}>
                      <QuotedReply variant="body2" color="text.secondary">
                        {composingMessage.content || '—'}
                      </QuotedReply>
                    </Box>
                  )}
                  {/* Keyed per opening, so each flag gets its own idempotency key. */}
                  <FlagComposer
                    key={composingFor.messageId ?? 'conversation'}
                    conversationId={conversationId}
                    messageId={composingFor.messageId}
                    onClose={() => setComposingFor(null)}
                  />
                </Stack>
              </FlagPanel>
            )}
          </Body>
        </>
      )}
    </Stack>
  );
}
